import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import FozzilPayCheckout from "./FozzilPayCheckout";
import { motion, AnimatePresence } from "framer-motion";

const PricingMedos = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutProduct, setCheckoutProduct] = useState({
    amount: "1.50",
    name: "Medos Test Plan",
    isRecurring: false
  });


  const handlePaymentSuccess = (data) => {
    console.log("Payment successful:", data);
    window.location.href = '/products/medos';
  };

  return (
    <>
      <div className="bg-black text-white font-secondary min-h-[100dvh] flex flex-col box-border relative overflow-x-hidden">
        {/* Cinematic Background Elements */}
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-cyan-600/10 blur-[150px] rounded-full -translate-y-1/2" />
          <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-blue-600/5 blur-[180px] rounded-full translate-y-1/2" />
        </div>

        <button
          onClick={() => navigate("/")}
          className="fixed top-4 md:top-8 right-4 md:right-8 z-50 w-12 h-12 flex items-center justify-center text-white/50 hover:text-white transition-all duration-300 hover:bg-white/10 rounded-full border border-white/5 backdrop-blur-md"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <section className="text-center pt-24 md:pt-32 pb-8 max-w-4xl mx-auto px-6 relative z-10">
          <motion.h4 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-cyan-500 font-bold tracking-[0.4em] uppercase text-xs mb-4"
          >
            Clinical Infrastructure
          </motion.h4>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-primary text-3xl md:text-6xl uppercase mb-8 text-white tracking-tighter"
          >
            MEDOS Pricing
          </motion.h1>
          
          {/* Tab Switcher */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="flex justify-center p-1 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full w-fit mx-auto mb-8 md:mb-12"
          >
            <button
              onClick={() => navigate("/pricing-taels")}
              className="px-6 md:px-8 py-2 md:py-2.5 rounded-full transition-all duration-500 bg-transparent text-white/60 hover:text-white text-xs md:text-sm font-medium"
            >
              T.A.E.L.S
            </button>
            <button
              onClick={() => navigate("/pricing-medos")}
              className="px-6 md:px-8 py-2 md:py-2.5 rounded-full transition-all duration-500 bg-white text-black text-xs md:text-sm font-bold shadow-[0_0_20px_rgba(255,255,255,0.2)]"
            >
              MEDOS
            </button>
          </motion.div>
        </section>

        <div className="flex flex-col items-center flex-1 relative z-10 px-4 pb-20">
          <section className="flex justify-center gap-6 flex-wrap max-w-7xl mx-auto items-stretch">

            {/* Starter Plan */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="group bg-zinc-950/40 backdrop-blur-2xl border border-white/10 rounded-[32px] p-6 md:p-8 flex flex-col items-start text-left box-border w-full sm:w-[280px] transition-all duration-500 relative hover:border-cyan-500/30 hover:-translate-y-2 overflow-hidden"
            >
              <h2 className="font-primary text-xl uppercase text-white/50 group-hover:text-white transition-colors mb-2">Starter</h2>
              <p className="text-sm text-zinc-400 leading-relaxed mb-10 h-10">Essential tools for independent clinics.</p>
              <button className="w-full py-2.5 px-6 mb-10 text-sm font-black text-white rounded-full border border-cyan-500/50 hover:bg-cyan-500 hover:text-black transition-all duration-500 uppercase tracking-widest" onClick={() => navigate("/products/medos")}>Inquiry</button>
              <ul className="space-y-4 w-full">
                {['Core Billing & Inventory', 'GST-ready Invoicing', 'Basic Sales Reports'].map((feat, i) => (
                  <li key={i} className="flex items-center gap-3 text-xs text-zinc-400">
                    <div className="w-1 h-1 rounded-full bg-cyan-500" />
                    {feat}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Advanced Plan (Popular) */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              className="group bg-zinc-950 border-2 border-cyan-500 rounded-[32px] p-8 md:p-10 flex flex-col items-start text-left box-border w-full sm:w-[320px] transition-all duration-700 relative z-20 shadow-[0_0_50px_rgba(6,182,212,0.15)] hover:shadow-[0_0_80px_rgba(6,182,212,0.25)] hover:-translate-y-3 overflow-hidden"
            >
              <motion.div 
                 animate={{ x: ['-200%', '200%'] }}
                 transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", repeatDelay: 2 }}
                 className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/[0.05] to-transparent skew-x-[35deg] pointer-events-none"
              />
              <div className="absolute top-6 right-6 bg-cyan-500/10 text-cyan-400 text-[10px] uppercase font-black px-3 py-1 rounded-full border border-cyan-500/20">
                Most Popular
              </div>
              <h2 className="font-primary text-2xl uppercase text-white mb-2 tracking-tight">Advanced</h2>
              <p className="text-sm text-zinc-400 leading-relaxed mb-10 h-10">AI-driven infrastructure for scaling hospitals.</p>
              <button className="w-full py-3.5 px-6 mb-10 text-sm font-black text-white rounded-full bg-gradient-to-r from-cyan-600 to-blue-600 shadow-[0_10px_30px_rgba(6,182,212,0.3)] hover:shadow-[0_15px_40px_rgba(6,182,212,0.5)] transition-all duration-500 uppercase tracking-widest" onClick={() => navigate("/products/medos")}>Inquiry</button>
              <ul className="space-y-4 w-full">
                {['Everything in Basic', 'ZYPHER AI Assistant', 'Advanced Analytics', 'Unlimited Staff'].map((feat, i) => (
                  <li key={i} className="flex items-center gap-4 text-sm text-zinc-300">
                    <div className="w-5 h-5 rounded-full bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20">
                      <svg className="w-3 h-3 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/></svg>
                    </div>
                    {feat}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Lifetime Plan */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
              className="group bg-zinc-950/40 backdrop-blur-2xl border border-white/10 rounded-[32px] p-6 md:p-8 flex flex-col items-start text-left box-border w-full sm:w-[280px] transition-all duration-500 relative hover:border-cyan-500/30 hover:-translate-y-2 overflow-hidden"
            >
              <h2 className="font-primary text-xl uppercase text-white/50 group-hover:text-white transition-colors mb-2">Lifetime</h2>
              <p className="text-sm text-zinc-400 leading-relaxed mb-10 h-10">The Founder's Deal. Perpetual ownership.</p>
              <button className="w-full py-2.5 px-6 mb-10 text-sm font-black text-white rounded-full border border-white/10 hover:bg-white hover:text-black transition-all duration-500 uppercase tracking-widest" onClick={() => navigate("/products/medos")}>Inquiry</button>
              <ul className="space-y-4 w-full">
                {['Everything in Advanced', 'No Recurring Fees', 'Priority Legacy Support'].map((feat, i) => (
                  <li key={i} className="flex items-center gap-3 text-xs text-zinc-400">
                    <div className="w-1 h-1 rounded-full bg-cyan-500" />
                    {feat}
                  </li>
                ))}
              </ul>
            </motion.div>
          </section>
        </div>

        <footer className="relative z-10 text-center text-[10px] md:text-xs text-zinc-600 px-4 py-12 max-w-2xl mx-auto uppercase tracking-[0.1em]">
          By proceeding, you agree to FOZZIL's 
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="mx-2 text-zinc-400 hover:text-blue-500 transition-colors">Terms</a> 
          & 
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="ml-2 text-zinc-400 hover:text-blue-500 transition-colors">Privacy</a>.
        </footer>
      </div>

      <FozzilPayCheckout
        isOpen={showCheckout}
        onClose={() => setShowCheckout(false)}
        amount={checkoutProduct.amount}
        productinfo={checkoutProduct.name}
        isRecurring={checkoutProduct.isRecurring}
        user_id={user?.id || 'guest'}
        firstname={user?.user_metadata?.full_name || user?.email?.split('@')[0] || ''}
        email={user?.email || ''}
        onSuccess={handlePaymentSuccess}
        allowedMethods={['UPI', 'Card', 'Netbanking', 'Wallet']}
      />

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(6, 182, 212, 0.2); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(6, 182, 212, 0.4); }
      `}</style>
    </>
  );
};

export default PricingMedos;
